import { Bot, BriefcaseBusiness, CalendarCheck, CalendarClock, CalendarDays, EyeOff, LayoutGrid, MessageSquareText, MessagesSquare, ScanFace, Settings as SettingsIcon, ShieldCheck, Smartphone, UsersRound } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ScrollArea } from '@/components/ui/scroll-area';
import { usePermissions } from '@/hooks/use-permissions';
import { hasPermission, isAdminUser, isMenuItemHidden } from '@/lib/stores/permissions-store';
import { isWebMode } from '@/lib/config/web-mode';

// The Settings area is a list+detail: this left rail switches the active sub-page. Grouped into
// SHARED (tenant-wide config), THIS DEVICE (local to this machine, not synced), PATIENT QUEUE
// (queue definitions) and TEAM (employees + access groups). Entry types + transition events are
// per-queue, so they live as tabs inside the queue editor — not as rail items.
//
// `requires` gates the entry on an RBAC module/level (src/lib/permissions.js); entries without
// it are always visible. `menuKey` is the entry's key in the per-group "Menu visibility" list
// (MENU_ITEMS) — the same cosmetic trim the nav sidebar uses, so an access group can hide a
// Settings section it has rights to. `adminOnly` gates strictly on isAdmin (module checks pass
// for any manager, so they are NOT admin-exclusive); no entry uses it today — WhatsApp
// Connections moved onto the `integrations` module — but it stays as the escape hatch for a
// genuinely admin-exclusive surface. A group whose entries all filter out disappears entirely.
export const SETTINGS_SUB_PAGES = [
  { group: 'SHARED', id: 'preferences', label: 'Preferences', hint: 'Theme & shared settings', icon: SettingsIcon, menuKey: 'settings_preferences' },
  { group: 'SHARED', id: 'thread-groups', label: 'Thread groups', hint: 'Threads list tags', icon: MessagesSquare, menuKey: 'settings_thread_groups' },
  { group: 'SHARED', id: 'appointment-confirmation', label: 'Appointment confirmations', hint: 'Confirmation templates', icon: CalendarCheck, menuKey: 'settings_appointment_confirmation' },
  { group: 'SHARED', id: 'message-templates', label: 'Message templates', hint: 'Reusable replies & variables', icon: MessageSquareText, menuKey: 'settings_message_templates' },
  // No RBAC gate (like its SHARED siblings): readable by any employee, canEdit comes from the
  // payload's settings:manage answer.
  { group: 'SHARED', id: 'clinic-visibility', label: 'Clinic visibility', hint: 'Where each clinic appears', icon: EyeOff, menuKey: 'settings_clinic_visibility' },
  // No RBAC gate: face unlock is a personal, device-local convenience — it grants nothing the
  // user's own PIN doesn't already, so there is nothing here for a permission to protect.
  // `desktopOnly` hides the entry in web mode (`npm run web`): the enroll dialog would open
  // the camera even with the face-auth policy unregistered, so the surface must not exist.
  { group: 'THIS DEVICE', id: 'face-unlock', label: 'Face unlock', hint: 'Sign in by recognition', icon: ScanFace, desktopOnly: true, menuKey: 'settings_face_unlock' },
  { group: 'SCHEDULING', id: 'work-roles', label: 'Work roles', hint: 'Work Plan role groups', icon: BriefcaseBusiness, requires: { module: 'scheduling', level: 'view' }, menuKey: 'settings_work_roles' },
  { group: 'SCHEDULING', id: 'work-plan', label: 'Work plan', hint: 'Session hours & staff flags', icon: CalendarClock, requires: { module: 'scheduling', level: 'view' }, menuKey: 'settings_work_plan' },
  { group: 'SCHEDULING', id: 'calendars', label: 'Calendars', hint: 'Doctor, clinic & standalone calendars', icon: CalendarDays, requires: { module: 'scheduling', level: 'view' }, menuKey: 'settings_calendars' },
  { group: 'PATIENT QUEUE', id: 'queues', label: 'Queues', hint: 'Swimlanes & sub-categories', icon: LayoutGrid, requires: { module: 'queue_config', level: 'view' }, menuKey: 'settings_queues' },
  { group: 'INTEGRATIONS', id: 'bot-service-hours', label: 'Bot working hours', hint: 'Chatbot hours & auto-replies', icon: Bot, requires: { module: 'settings', level: 'view' }, menuKey: 'settings_bot_service_hours' },
  { group: 'INTEGRATIONS', id: 'whatsapp-connections', label: 'WhatsApp Connections', hint: 'Bot status & QR re-bind', icon: Smartphone, requires: { module: 'integrations', level: 'view' }, menuKey: 'settings_whatsapp_connections' },
  { group: 'TEAM', id: 'team-employees', label: 'Employees', hint: 'Accounts & access', icon: UsersRound, requires: { module: 'staff', level: 'manage' }, menuKey: 'settings_employees' },
  { group: 'TEAM', id: 'team-groups', label: 'Access groups', hint: 'Permission matrix', icon: ShieldCheck, requires: { module: 'staff', level: 'manage' }, menuKey: 'settings_access_groups' },
];

const GROUP_ORDER = ['SHARED', 'THIS DEVICE', 'SCHEDULING', 'PATIENT QUEUE', 'INTEGRATIONS', 'TEAM'];

// The id the rail falls back to when a group hides everything — see the floor in
// getVisibleSettingsSubPages.
export const DEFAULT_SETTINGS_SUB_PAGE = 'preferences';

// getVisibleSettingsSubPages — the rail's live entry list, shared with settings-page.jsx so the
// rail and the render switch can never disagree about what exists. Callers must be inside a
// component that reads usePermissions(), so the answer re-renders when rights change.
//
// The PREFERENCES FLOOR: menu keys only trim visibility and the Settings nav item is never
// hideable, so a group that hides every section must not land on an empty shell. Preferences is
// the only entry with no RBAC gate, which makes it the only entry that can always be the floor.
export function getVisibleSettingsSubPages() {
  const visible = SETTINGS_SUB_PAGES.filter(
    (p) =>
      (!p.desktopOnly || !isWebMode()) &&
      (!p.requires || hasPermission(p.requires.module, p.requires.level)) &&
      (!p.adminOnly || isAdminUser()) &&
      (!p.menuKey || !isMenuItemHidden(p.menuKey)),
  );
  if (visible.length > 0) return visible;
  return SETTINGS_SUB_PAGES.filter((p) => p.id === DEFAULT_SETTINGS_SUB_PAGE);
}

function SubItem({ item, active, onSelect }) {
  const Icon = item.icon;
  return (
    <button
      type="button"
      onClick={() => onSelect(item.id)}
      aria-current={active ? 'page' : undefined}
      className={cn(
        'flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        active
          ? 'bg-accent text-accent-foreground shadow-[inset_0_0_0_1px_var(--border)]'
          : 'text-foreground hover:bg-accent/50',
      )}
    >
      <Icon
        className={cn('h-4 w-4 shrink-0', active ? 'text-accent-foreground' : 'text-muted-foreground')}
        aria-hidden
      />
      <span className="min-w-0">
        <span className={cn('block truncate text-[13px]', active ? 'font-semibold' : 'font-medium')}>
          {item.label}
        </span>
        {item.hint ? (
          <span className="block truncate text-[11px] text-muted-foreground">{item.hint}</span>
        ) : null}
      </span>
    </button>
  );
}

export function SettingsSubRail({ active, onSelect }) {
  // Live RBAC state so permission-gated entries appear/disappear without a remount.
  usePermissions();
  const visibleSubPages = getVisibleSettingsSubPages();
  return (
    <nav
      aria-label="Settings sections"
      className="flex min-h-0 w-60 shrink-0 flex-col border-r border-border"
    >
      <ScrollArea className="min-h-0 flex-1">
        <div className="flex flex-col gap-1 p-3.5">
      <div className="px-2.5 pb-1.5">
        <div className="text-lg font-extrabold tracking-tight text-foreground">Settings</div>
        <div className="mt-0.5 text-xs text-muted-foreground">Workspace configuration</div>
      </div>
      {GROUP_ORDER.map((group) => {
        const items = visibleSubPages.filter((p) => p.group === group);
        if (items.length === 0) return null;
        return (
          <div key={group} className="mt-2 flex flex-col gap-0.5">
            <div className="px-2.5 pb-1 pt-2 text-[10.5px] font-extrabold tracking-wide text-muted-foreground">
              {group}
            </div>
            {items.map((item) => (
              <SubItem key={item.id} item={item} active={active === item.id} onSelect={onSelect} />
            ))}
          </div>
        );
      })}
        </div>
      </ScrollArea>
    </nav>
  );
}
